import { useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { buildSDPSearchPayload } from "app/utils/smartDPUtils";
import {
	QUOTATION_CODE_STATUS,
	RESOLUTION,
	SDP_PRODUCTS_FILTERS_KEY,
	STOPOVER_DIRECTIONS,
} from "app/constants";
import { unsetSDPProductsFilter } from "app/pages/SmartDP/Listing/SmartDPFilters/smartDPProductsFilterActionCreators";
import PropTypes from "prop-types";
import get from "lodash/get";
import { addHours } from "date-fns";
import { getAvailableStopoverCities } from "app/pages/SmartDP/smartDPSelectors";
import { bindActionCreators } from "redux";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import StopoverSearchForm from "app/pages/Stopover/StopoverSearchForm/StopoverSearchForm";
import { fetchAllStopoverCities } from "app/pages/Stopover/stopoverActionCreators";

const StopoverSearchFormContainer = props => {
	const { unsetSDPProductsFilter, onSubmit } = props;

	const navigate = useNavigate();

	const handleFormSubmission = useCallback(({ values, pathname = "" }) => {
		const queryParams = buildSDPSearchPayload(values);
		unsetSDPProductsFilter(SDP_PRODUCTS_FILTERS_KEY.BUDGET);

		if (onSubmit) {
			onSubmit(values);
		}

		if (pathname.includes("/stopover/listing")) {
			navigate({ pathname: pathname, search: queryParams }, { replace: true });
		} else {
			navigate({ pathname: "/stopover/listing", search: queryParams });
		}
	}, []);

	return (
		<div className="search-form__container">
			<StopoverSearchForm {...props} onSuccess={handleFormSubmission} />
		</div>
	);
};

StopoverSearchFormContainer.propTypes = {
	unsetSDPProductsFilter: PropTypes.func,
	fetchAllStopoverCities: PropTypes.func,
	isMobile: PropTypes.bool,
	brand: PropTypes.string,
	disableFormPersistance: PropTypes.bool, // used to avoid fields flickering when coming from merch page with sdp form to home sdp
	onSubmit: PropTypes.func,
};

const mapStateToProps = (state, props) => {
	const booking = state.booking;

	const initialValues = {
		travelDates: {
			departureDate: "",
			endDate: "",
		},
	};

	if (get(state, "smartDP.cabins[0]")) {
		initialValues.cabin = get(state, "smartDP.cabins[0]");
	}

	if (booking.cabin?.code) {
		initialValues.cabin = booking.cabin;
	}

	if (
		!props.disableFormPersistance &&
		booking.destinationResort?.code &&
		state.smartDP.destinations
	) {
		const destination = state.smartDP.destinations.find(
			destination => destination.code.toString() === booking.destinationResort.code
		);
		if (destination) {
			initialValues.destinationResort = {
				...booking.destinationResort,
				departureDateStart: destination.departureDateStart,
				departureDateEnd: destination.departureDateEnd,
			};
		}
	}

	if (!props.disableFormPersistance && booking.departureCity?.code) {
		initialValues.departureCity = booking.departureCity;
	}

	if (!props.disableFormPersistance && booking.departureDate) {
		// @see SmartDPSearch in handleFormSubmission
		// ex Paris : Fri Jun 11 2021 02:00:00 GMT+0200 (heure d’été d’Europe centrale)
		let departureDate = new Date(booking.departureDate);
		// => Fri Jun 11 2021 00:00:00 GMT+0200 (heure d’été d’Europe centrale)
		departureDate = addHours(departureDate, departureDate.getTimezoneOffset() / 60);
		// departureDate.geTime() gives 10/6/2021 à 22:00:00
		initialValues.travelDates.departureDate = departureDate;
	}

	if (!props.disableFormPersistance && booking.endDate) {
		// @see SmartDPSearch in handleFormSubmission
		// ex Paris : Fri Jun 11 2021 02:00:00 GMT+0200 (heure d’été d’Europe centrale)
		let endDate = new Date(booking.endDate);
		// => Fri Jun 11 2021 00:00:00 GMT+0200 (heure d’été d’Europe centrale)
		endDate = addHours(endDate, endDate.getTimezoneOffset() / 60);
		// endDate.geTime() gives 10/6/2021 à 22:00:00
		initialValues.travelDates.endDate = endDate;
	}

	if (booking.occupancies?.length > 0) {
		initialValues.occupancies = booking.occupancies;
	}

	if (!props.disableFormPersistance && booking.duration?.value) {
		initialValues.duration = {
			code: booking.duration.value,
		};
	}

	const stopoverCities = props.stopoverCities || getAvailableStopoverCities(state);

	if (stopoverCities?.length > 0) {
		const stopoverCity =
			booking.stopoverCityId && !props.disableFormPersistance
				? stopoverCities.find(city => city.code === booking.stopoverCityId)
				: stopoverCities[0];

		initialValues.stopoverCity = {
			code: stopoverCity?.code,
			label: stopoverCity?.label,
		};
	}

	const stopoverDirections = [
		{
			label: props.intl.formatMessage({ id: "sdp.search.stopover.outbound.label" }),
			code: STOPOVER_DIRECTIONS.OUTBOUND,
		},
		{
			label: props.intl.formatMessage({ id: "sdp.search.stopover.inbound.label" }),
			code: STOPOVER_DIRECTIONS.INBOUND,
		},
	];

	if (!props.disableFormPersistance && booking.stopoverDirection) {
		initialValues.stopoverDirection = {
			code: booking.stopoverDirection,
			label: stopoverDirections.find(
				direction => direction.code === booking.stopoverDirection
			).label,
		};
	} else {
		initialValues.stopoverDirection = {
			label: props.intl.formatMessage({ id: "sdp.search.stopover.outbound.label" }),
			code: STOPOVER_DIRECTIONS.OUTBOUND,
		};
	}

	return {
		initialValues,
		destinations: state.smartDP.destinations,
		departureCities: state.smartDP.departureCities,
		stopoverCities: stopoverCities,
		stopoverDirections,
		cabins: state.smartDP.cabins,
		validateOnMount: state.smartDP.status === QUOTATION_CODE_STATUS.BAD_REQUEST,
		isMobile: [RESOLUTION.SMALL, RESOLUTION.MEDIUM].includes(state?.resolution),
		brand: state.brand.code,
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators({ unsetSDPProductsFilter, fetchAllStopoverCities }, dispatch);
};

export default injectIntl(
	connect(
		mapStateToProps,
		mapDispatchToProps
	)(StopoverSearchFormContainer)
);
